import React, { useEffect, useMemo, useState } from 'react'
import { OptionWrapper } from 'src/editor/layout/sidebar/settings/option-wrapper'
import { TextAreaControl } from 'src/editor/shared/settings/controls/text-area-control'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { CheckboxNew } from 'src/editor/shared/ui-lib/checkbox-new/checkbox-new'
import { DatePickerControl } from 'src/editor/shared/ui-lib/date-time-picker'
import { ModalWindow } from 'src/editor/shared/ui-lib/modal-window'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { BlogPostImageInput } from './blog-post-image-input'
import { BlogPostModalFields } from './blog-post-modal.schema'

interface BlogPostModalProps {
  isVisible: boolean
  initialValues?: BlogPostModalFields
  categories?: {
    id: string
    label: string
  }[]
  isEditing: boolean
  onClose: () => void
  onSubmit: (values: BlogPostModalFields, imageBlob?: Blob) => void
}

export const BlogPostModal = ({
  isVisible,
  initialValues,
  categories = [],
  isEditing,
  onClose,
  onSubmit,
}: BlogPostModalProps) => {
  const { t } = useTypedTranslation()
  const [title, setTitle] = useState('')
  const [shortDescription, setShortDescription] = useState('')
  const [urlPath, setUrlPath] = useState('')
  const [publicationDate, setPublicationDate] = useState<Date>(new Date())
  const [imageSrc, setImageSrc] = useState('')
  const [categoryIds, setCategoryIds] = useState<string[]>([])
  const [imageBlob, setImageBlob] = useState<Blob | undefined>(undefined)

  useEffect(() => {
    if (!isVisible) {
      return
    }
    const initialBlogPostValues = initialValues
    setTitle(initialBlogPostValues?.title ?? '')
    setShortDescription(initialBlogPostValues?.shortDescription ?? '')
    setUrlPath(initialBlogPostValues?.urlPath ?? '')
    setPublicationDate(
      initialBlogPostValues?.publicationDate
        ? new Date(initialBlogPostValues.publicationDate)
        : new Date(),
    )
    setImageSrc(initialBlogPostValues?.imageSrc ?? '')
    setCategoryIds(initialBlogPostValues?.categoryIds ?? [])
    setImageBlob(undefined)
  }, [isVisible, initialValues])

  const modalTitle = useMemo(
    () => (isEditing ? 'Edit Blog Post' : 'Create Blog Post'),
    [isEditing],
  )

  const handleCropComplete = (blobUrl: string, blob: Blob) => {
    setImageSrc(blobUrl)
    setImageBlob(blob)
  }

  const handleRemoveImage = () => {
    setImageSrc('')
    setImageBlob(undefined)
  }

  const toggleCategory = (categoryId: string, checked: boolean) => {
    setCategoryIds(currentCategoryIds =>
      checked
        ? [...currentCategoryIds, categoryId]
        : currentCategoryIds.filter(currentId => currentId !== categoryId),
    )
  }

  const handleSubmit = () => {
    onSubmit(
      {
        title,
        shortDescription,
        urlPath,
        publicationDate: publicationDate.toISOString(),
        imageSrc,
        categoryIds,
      },
      imageBlob,
    )
  }

  return (
    <ModalWindow
      isOpened={isVisible}
      onClose={onClose}
      title={modalTitle}
      cancelButtonProps={{ onClick: onClose, children: t('global.cancel') }}
      confirmButtonProps={{
        onClick: handleSubmit,
        children: t('components.core.code_editor.save'),
      }}
    >
      <TextInputControl
        wrapperProps={{
          className: 'mt-1 mb-3',
          labelText: 'Title',
          required: true,
          skipTranslation: true,
          helpMessage: t(
            'website_settings.sidebar.blog.modal.fields.title.help_message',
          ),
        }}
        id="blog-post-title"
        value={title}
        update={setTitle}
      />
      <TextAreaControl
        wrapperProps={{
          className: 'mt-1 mb-3',
          labelText: 'Short description',
          required: true,
          skipTranslation: true,
          helpMessage: t(
            'website_settings.sidebar.blog.modal.fields.short_description.help_message',
          ),
        }}
        id="blog-post-short-description"
        value={shortDescription}
        update={setShortDescription}
      />
      <TextInputControl
        wrapperProps={{
          className: 'mt-1 mb-3',
          labelText: 'URL Path',
          required: true,
          skipTranslation: true,
          helpMessage: t(
            'website_settings.sidebar.blog.modal.fields.url_path.help_message',
          ),
        }}
        id="blog-post-url-path"
        value={urlPath}
        update={setUrlPath}
      />
      <OptionWrapper
        className="mt-1 mb-3"
        labelText="Blog post publication date to be displayed"
        required
        skipTranslation
        helpMessage={t(
          'website_settings.sidebar.blog.modal.fields.publication_date.help_message',
        )}
      >
        <DatePickerControl
          dateTime={publicationDate}
          updateDate={setPublicationDate}
        />
      </OptionWrapper>
      <OptionWrapper
        className="mt-1 mb-3"
        labelText="Image"
        skipTranslation
        helpMessage={t(
          'website_settings.sidebar.blog.modal.fields.image.help_message',
        )}
      >
        <BlogPostImageInput
          imageSrc={imageSrc}
          onCropComplete={handleCropComplete}
          onRemove={handleRemoveImage}
        />
      </OptionWrapper>
      {categories.length > 0 && (
        <OptionWrapper
          className="mt-1 mb-3"
          labelText={t('website_settings.sidebar.blog.content.categories')}
          skipTranslation
        >
          <div className="grid grid-cols-4 gap-2">
            {categories.map(category => (
              <CheckboxNew
                key={category.id}
                wrapperClassName="items-start"
                checked={categoryIds.includes(category.id)}
                onChange={checked => toggleCategory(category.id, checked)}
                label={
                  <span className="whitespace-normal break-words">
                    {category.label}
                  </span>
                }
                skipTranslation
              />
            ))}
          </div>
        </OptionWrapper>
      )}
    </ModalWindow>
  )
}
